import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { goApi } from '../../services';
import { apiRoutes } from '../../services/apiRoutes';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Box,
    Pagination,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { formatDate } from '../../date.utilitites';
import UserInfo from '../UserInfo';
import DeleteAssignedUserButton from './DeleteAsssignedUser';

const AssignedUserList = ({ type, typeId, refreshKey }: any) => {
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const notify = useNotify();
    const limit = 10;

    const fetchUsers = async (currentPage: number, search: string = '') => {
        setLoading(true);
        try {
            const searchParam = search ? `&name=${encodeURIComponent(search)}` : '';
            const resp = await goApi.get(
                `${apiRoutes.GET_ECOM_USERS(
                    type,
                    typeId,
                )}?page=${currentPage}&limit=${limit}${searchParam}`,
            );
            setUsers(resp.data?.items || []);
            setTotal(resp.data?.pagination?.total || 0);
            setTotalPages(Math.ceil((resp.data?.pagination?.total || 0) / limit));
        } catch (e) {
            console.error('Failed to load users', e);
            setUsers([]);
            setTotal(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!searchQuery.trim()) {
                fetchUsers(page, searchQuery);
                return;
            }

            const startsWithDigit = /^\d/.test(searchQuery);

            if (startsWithDigit) {
                const digitsOnly = searchQuery.replace(/\D/g, '');
                if (digitsOnly.length === 10) {
                    fetchUsers(page, searchQuery);
                }
            } else {
                fetchUsers(page, searchQuery);
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [type, typeId, page, refreshKey, searchQuery]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setPage(1);
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
                <TextField
                    size="small"
                    placeholder="Search by name or phone"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: '250px' }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Student</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Assigned By</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Order Id</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Assigned Date</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Expires On</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Action</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <Typography variant="h6" color="textSecondary">
                                        No assigned users found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((item: any) => {
                                const student = item?.student;
                                const isExpired =
                                    item?.endsOn && new Date(item.endsOn) < new Date();

                                return (
                                    <TableRow key={item?.id} hover>
                                        <TableCell>
                                            <UserInfo user={student} />
                                        </TableCell>
                                        <TableCell>
                                            <UserInfo user={item?.assignedByUser} />
                                        </TableCell>
                                        <TableCell>
                                            {item?.orderId && (
                                                <Tooltip title={`Order ID: ${item.orderId}`}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(
                                                                item.orderId.toString(),
                                                            );
                                                            notify('app.copied', {
                                                                type: 'info',
                                                                messageArgs: {
                                                                    _: 'Copied to clipboard',
                                                                },
                                                            });
                                                        }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>{formatDate(item?.createdAt)}</TableCell>
                                        <TableCell>
                                            <p
                                                className={
                                                    isExpired
                                                        ? 'bg-red-50 text-red-800 p-2 font-semibold'
                                                        : ''
                                                }
                                            >
                                                {formatDate(item?.endsOn)}
                                            </p>
                                        </TableCell>
                                        <TableCell>
                                            <DeleteAssignedUserButton
                                                type={type}
                                                typeId={typeId}
                                                userId={student?.id}
                                                record={student}
                                                onSuccess={() => fetchUsers(page, searchQuery)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Box>
    );
};

export default AssignedUserList;
